'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { cn } from 'cn';
import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
type FieldSetProps = React.ComponentProps<'fieldset'>;
export function FieldSet(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex flex-col gap-6 has-[>[data-slot=checkbox-group]]:gap-3 has-[>[data-slot=radio-group]]:gap-3", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <fieldset data-slot="field-set" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldLegendProps = {
  variant?: 'legend' | 'label';
} & React.ComponentProps<'legend'>;
export function FieldLegend(props) {
  const $ = _c(10);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      variant: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const variant = t0 === undefined ? "legend" : t0;
  let t1;
  if ($[4] !== className) {
    t1 = cn("mb-3 font-medium data-[variant=label]:text-sm data-[variant=legend]:text-base", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== rest || $[7] !== t1 || $[8] !== variant) {
    t2 = <legend data-slot="field-legend" data-variant={variant} className={t1} {...rest} />;
    $[6] = rest;
    $[7] = t1;
    $[8] = variant;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
}
type FieldGroupProps = React.ComponentProps<'div'>;
export function FieldGroup(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-group @container/field-group flex w-full flex-col gap-7", "data-[slot=checkbox-group]:gap-3", "*:data-[slot=field-group]:gap-4", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-group" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
const fieldVariants = cva('group/field flex w-full gap-3 data-[invalid=true]:text-destructive', {
  variants: {
    orientation: {
      vertical: 'flex-col *:w-full [&>.sr-only]:w-auto',
      horizontal: ['flex-row items-center', 'has-[>[data-slot=field-content]]:items-start', '*:data-[slot=field-label]:flex-auto', 'has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px'],
      responsive: ['flex-col', '*:w-full', '@md/field-group:flex-row @md/field-group:items-center', '@md/field-group:*:w-auto', '@md/field-group:has-[>[data-slot=field-content]]:items-start', '@md/field-group:*:data-[slot=field-label]:flex-auto', '[&>.sr-only]:w-auto', '@md/field-group:has-[>[data-slot=field-content]]:[&>[role=checkbox],[role=radio]]:mt-px']
    }
  },
  defaultVariants: {
    orientation: 'vertical'
  }
});
type FieldProps = React.ComponentProps<'div'> & VariantProps<typeof fieldVariants>;
export function Field(props) {
  const $ = _c(11);
  let className;
  let rest;
  let t0;
  if ($[0] !== props) {
    ({
      className,
      orientation: t0,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
    $[3] = t0;
  } else {
    className = $[1];
    rest = $[2];
    t0 = $[3];
  }
  const orientation = t0 === undefined ? "vertical" : t0;
  let t1;
  if ($[4] !== className || $[5] !== orientation) {
    t1 = cn(fieldVariants({
      orientation
    }), className);
    $[4] = className;
    $[5] = orientation;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== orientation || $[8] !== rest || $[9] !== t1) {
    t2 = <div role="group" data-slot="field" data-orientation={orientation} className={t1} {...rest} />;
    $[7] = orientation;
    $[8] = rest;
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  return t2;
}
type FieldContentProps = React.ComponentProps<'div'>;
export function FieldContent(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-content flex flex-1 flex-col gap-1 leading-snug", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-content" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldLabelProps = React.ComponentProps<typeof Label>;
export function FieldLabel(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("group/field-label peer/field-label flex w-fit gap-2 leading-snug", "group-data-[disabled=true]/field:opacity-50", "has-data-checked:border-primary/30 has-data-checked:bg-primary/5", "has-[>[data-slot=field]]:rounded-xl has-[>[data-slot=field]]:border", "has-[>[data-slot=field]]:not-has-[:disabled,[data-disabled]]:hover:bg-input/40", "has-[>[data-slot=field]]:has-focus-visible:border-ring has-[>[data-slot=field]]:has-focus-visible:ring-[3px] has-[>[data-slot=field]]:has-focus-visible:ring-ring/50", "*:data-[slot=field]:p-4", "dark:has-data-checked:border-primary/20 dark:has-data-checked:bg-primary/10", "has-[>[data-slot=field]]:w-full has-[>[data-slot=field]]:flex-col", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <Label data-slot="field-label" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldTitleProps = React.ComponentProps<'div'>;
export function FieldTitle(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("flex w-fit items-center gap-2 text-sm font-medium group-data-[disabled=true]/field:opacity-50", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <div data-slot="field-label" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldDescriptionProps = React.ComponentProps<'p'>;
export function FieldDescription(props) {
  const $ = _c(8);
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = className;
    $[2] = rest;
  } else {
    className = $[1];
    rest = $[2];
  }
  let t0;
  if ($[3] !== className) {
    t0 = cn("text-left text-sm/normal font-normal text-muted-foreground", "group-has-data-horizontal/field:text-balance", "[[data-variant=legend]+&]:-mt-1.5", "last:mt-0 nth-last-2:-mt-1", "[&>a]:underline [&>a]:underline-offset-4 [&>a:hover]:text-primary", className);
    $[3] = className;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  let t1;
  if ($[5] !== rest || $[6] !== t0) {
    t1 = <p data-slot="field-description" className={t0} {...rest} />;
    $[5] = rest;
    $[6] = t0;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  return t1;
}
type FieldSeparatorProps = {
  children?: React.ReactNode;
} & React.ComponentProps<'div'>;
export function FieldSeparator(props) {
  const $ = _c(14);
  let children;
  let className;
  let rest;
  if ($[0] !== props) {
    ({
      children,
      className,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = rest;
  } else {
    children = $[1];
    className = $[2];
    rest = $[3];
  }
  const t0 = !!children;
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative -my-2 h-5 text-sm group-data-[variant=outline]/field-group:-mb-2", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Separator className="absolute inset-0 top-1/2" />;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== children) {
    t3 = children && <span className="relative mx-auto block w-fit bg-background px-2 text-muted-foreground" data-slot="field-separator-content">{children}</span>;
    $[7] = children;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== rest || $[10] !== t0 || $[11] !== t1 || $[12] !== t3) {
    t4 = <div data-slot="field-separator" data-content={t0} className={t1} {...rest}>{t2}{t3}</div>;
    $[9] = rest;
    $[10] = t0;
    $[11] = t1;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
type FieldErrorProps = {
  errors?: Array<{
    message?: string;
  } | undefined>;
} & React.ComponentProps<'div'>;
export function FieldError(props) {
  const $ = _c(15);
  let children;
  let className;
  let errors;
  let rest;
  if ($[0] !== props) {
    ({
      className,
      children,
      errors,
      ...rest
    } = props);
    $[0] = props;
    $[1] = children;
    $[2] = className;
    $[3] = errors;
    $[4] = rest;
  } else {
    children = $[1];
    className = $[2];
    errors = $[3];
    rest = $[4];
  }
  let t0;
  bb0: {
    if (children) {
      t0 = children;
      break bb0;
    }
    if (!errors?.length) {
      t0 = null;
      break bb0;
    }
    let t1;
    if ($[5] !== errors) {
      t1 = [...new Map(errors.map(_temp)).values()];
      $[5] = errors;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    const uniqueErrors = t1;
    if (uniqueErrors.length === 1) {
      t0 = uniqueErrors[0]?.message;
      break bb0;
    }
    let t2;
    if ($[7] !== uniqueErrors) {
      t2 = <ul className="ml-4 flex list-disc flex-col gap-1">{uniqueErrors.map(_temp2)}</ul>;
      $[7] = uniqueErrors;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t0 = t2;
  }
  const content = t0;
  if (!content) {
    return null;
  }
  let t1;
  if ($[9] !== className) {
    t1 = cn("text-sm font-normal text-destructive", className);
    $[9] = className;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] !== content || $[12] !== rest || $[13] !== t1) {
    t2 = <div role="alert" data-slot="field-error" className={t1} {...rest}>{content}</div>;
    $[11] = content;
    $[12] = rest;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
function _temp2(error_0) {
  return error_0?.message && <li key={error_0.message}>{error_0.message}</li>;
}
function _temp(error) {
  return [error?.message, error];
}